import { useState, type ReactNode } from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useAuth, useLogout } from 'src/hooks/useAuth';

import { getUserDisplayName } from 'src/utils/get-user-display-name';

import { DASHBOARD_CONTENT_MAX_WIDTH } from 'src/constants';

import { NavBar, MobileNavBar } from 'src/components/navbar';
import {
  ProfileColourModal,
  DEFAULT_PROFILE_COLOUR,
} from 'src/components/profile-colour-modal/ProfileColourModal';

import DashboardNavMenu from './NavMenu';

type DashboardLayoutProps = {
  children: ReactNode;
};

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const [menuOpen, setMenuOpen] = useState(false);
  const [colourModalOpen, setColourModalOpen] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { logout } = useLogout();
  const displayName = getUserDisplayName(user);
  const userInitial = displayName.trim().charAt(0).toUpperCase() || '?';
  const menuId = 'dashboard-navigation-menu';
  // Desktop: full-width top bar + content centered on the page background.
  if (isDesktop) {
    return (
      <>
        <NavBar
          isAuthenticated={isAuthenticated}
          user={user}
          onSignOut={logout}
          avatarColour={user?.profileColour ?? DEFAULT_PROFILE_COLOUR}
          onAvatarClick={() => setColourModalOpen(true)}
        />
        <Box component="main" sx={{ flex: 1, width: '100%', overflowY: 'auto' }}>
          <Box
            sx={{
              maxWidth: DASHBOARD_CONTENT_MAX_WIDTH,
              mx: 'auto',
              width: '100%',
              px: { md: 4, lg: 8 },
              py: 0,
            }}
          >
            {children}
          </Box>
        </Box>

        <ProfileColourModal open={colourModalOpen} onClose={() => setColourModalOpen(false)} />
      </>
    );
  }

  // Mobile: phone-style card with the dashboard nav bar + hamburger -> nav overlay.
  return (
    <>
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          paddingTop: '1.5rem',
        }}
      >
        <MobileNavBar
          variant="dashboard"
          userName={displayName}
          userInitial={userInitial}
          avatarColour={user?.profileColour ?? DEFAULT_PROFILE_COLOUR}
          onAvatarClick={() => setColourModalOpen(true)}
          onRightClick={() => setMenuOpen(true)}
          menuOpen={menuOpen}
          menuId={menuId}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            px: 3,
            pb: 5,
          }}
        >
          {children}
        </Box>
      </Box>

      <ProfileColourModal open={colourModalOpen} onClose={() => setColourModalOpen(false)} />

      {menuOpen && (
        <DashboardNavMenu
          userName={displayName}
          userInitial={userInitial}
          avatarColour={user?.profileColour ?? DEFAULT_PROFILE_COLOUR}
          onClose={() => setMenuOpen(false)}
          onSignOut={logout}
          id={menuId}
        />
      )}
    </>
  );
}
